import React from 'react';
import { UserIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const RemittanceDetailHeader = ({ ownerReport, ownerCode, weekKey }) => {
    const calculateAggregates = () => {
        if (!ownerReport || !ownerReport.cars) return null;

        const totals = {
            total_remittance: 0,
            total_charges: 0,
            total_payout: 0,
            net_income: 0,
            car_count: ownerReport.cars.length,
            status_counts: {
                PENDING: 0,
                PROCESSED: 0,
                APPROVED: 0,
                PAID: 0
            }
        };

        ownerReport.cars.forEach(car => {
            totals.total_remittance += parseFloat(car.wkr_total || 0);
            totals.total_charges += parseFloat(car.expenses_total || 0);
            totals.total_payout += parseFloat(car.owner_payout || 0);
            totals.net_income += parseFloat(car.net_income || 0);

            if (car.status) {
                totals.status_counts[car.status] = (totals.status_counts[car.status] || 0) + 1;
            }
        });

        return totals;
    };

    const aggregates = calculateAggregates();

    if (!aggregates) {
        return <div className="text-center text-gray-500">No data available</div>;
    }

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            PROCESSED: 'bg-blue-100 text-blue-800',
            APPROVED: 'bg-green-100 text-green-800',
            PAID: 'bg-purple-100 text-purple-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            {/* Owner Info Section */}
            <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#FF5733] to-[#e64520] rounded-full flex items-center justify-center">
                            <UserIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-[#2a2e30]">
                                {ownerReport.owner_name || ownerCode}
                            </h2>
                            <p className="text-gray-600">Owner Code: {ownerCode}</p>
                            {ownerReport.owner_email && (
                                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                    <EnvelopeIcon className="w-4 h-4" />
                                    {ownerReport.owner_email}
                                </div>
                            )}
                            {ownerReport.owner_phone && (
                                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                    <PhoneIcon className="w-4 h-4" />
                                    {ownerReport.owner_phone}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 md:mt-0 text-right">
                        <div className="text-sm text-gray-600">Week</div>
                        <div className="text-3xl font-bold text-[#FF5733]">{weekKey}</div>
                        <div className="text-sm text-gray-600 mt-1">
                            {aggregates.car_count} {aggregates.car_count === 1 ? 'Vehicle' : 'Vehicles'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Aggregate Totals Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-blue-800 mb-1">Total Remittance</div>
                    <div className="text-2xl font-bold text-blue-900 text-right">
                        {formatCurrency(aggregates.total_remittance)}
                    </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-red-800 mb-1">Total Charges</div>
                    <div className="text-2xl font-bold text-red-900 text-right">
                        {formatCurrency(aggregates.total_charges)}
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-green-800 mb-1">Net Income</div>
                    <div className="text-2xl font-bold text-green-900 text-right">
                        {formatCurrency(aggregates.net_income)}
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-purple-800 mb-1">Owner Payout</div>
                    <div className="text-2xl font-bold text-purple-900 text-right">
                        {formatCurrency(aggregates.total_payout)}
                    </div>
                </div>
            </div>

            {/* Status Distribution */}
            <div className="flex flex-wrap gap-3 items-center">
                <span className="text-sm font-semibold text-gray-700">Status Distribution:</span>
                {Object.entries(aggregates.status_counts).map(([status, count]) => (
                    count > 0 && (
                        <span
                            key={status}
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(status)}`}
                        >
                            {status}: {count}
                        </span>
                    )
                ))}
            </div>
        </div>
    );
};

export default RemittanceDetailHeader;
